import i18n from "i18next";
import { Box } from "@mui/material";
import { useRef, useState } from "react";
import { CustomFunctionUiActionApi } from "src/api/generated/erp/customFunctions/triggers/uiAction/api/customFunctionUiActionApi.ts";
import type { CustomFunctionUiActionContext } from "src/api/generated/erp/customFunctions/triggers/uiAction/context/customFunctionUiActionContext.ts";
import type { CustomFunctionUiActionExecutionResult } from "src/api/generated/erp/customFunctions/triggers/uiAction/customFunctionUiActionExecutionResult.ts";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import type { CloseDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";
import { AavoFormActionButtons } from "src/components/common/forms/AavoFormActionButtons.tsx";
import {
	JsonSchemaObjectForm,
	type JsonSchemaObjectFormRef,
} from "src/components/common/forms/jsonSchema/JsonSchemaObjectForm.tsx";
import {
	type JsonObject,
	toJsonObject,
	toRjsfSchema,
} from "src/components/common/forms/jsonSchema/jsonSchemaObjectInputUtils.ts";
import type { CustomFunctionFileInput } from "src/api/generated/erp/customFunctions/model/customFunctionFileInput.ts";
import {
	CustomFunctionFileInputs,
	type CustomFunctionFileInputsRef,
} from "src/components/common/customFunctions/CustomFunctionFileInputs.tsx";
import {
	uploadCustomFunctionInputFiles,
	withoutCustomFunctionFileSchemaFields,
} from "src/components/common/customFunctions/customFunctionFileInputUtils.ts";
import type { CustomFunctionUserInputPresentation } from "src/api/generated/erp/customFunctions/model/customFunctionUserInputPresentation.ts";
import { createCustomFunctionUserInputUiSchema } from "src/components/common/customFunctions/customFunctionUserInputPresentation.ts";
import {
	createInitialCustomFunctionUiActionUserInput,
	prepareCustomFunctionUiActionUserInput,
} from "./customFunctionUiActionInput.ts";
import {
	createCustomFunctionUiActionIdempotencySession,
	type CustomFunctionUiActionIdempotencySession,
} from "./customFunctionUiActionIdempotency.ts";
import { CustomFunctionUiActionContinuation } from "./CustomFunctionUiActionContinuation.tsx";

interface CustomFunctionUiActionInputDialogProps {
	principalId: string;
	environmentId: string | null;
	triggerLineageId: string;
	context: CustomFunctionUiActionContext;
	userInputSchema: unknown;
	userInputPresentations: Record<string, CustomFunctionUserInputPresentation>;
	fileInputs: CustomFunctionFileInput[];
	closeDialog: CloseDialogFunc;
	onEdited: () => void;
	onExecuted: (result: CustomFunctionUiActionExecutionResult) => Promise<void>;
}

export function CustomFunctionUiActionInputDialog({
	principalId,
	environmentId,
	triggerLineageId,
	context,
	userInputSchema,
	userInputPresentations,
	fileInputs,
	closeDialog,
	onEdited,
	onExecuted,
}: CustomFunctionUiActionInputDialogProps) {
	const schema = toRjsfSchema(withoutCustomFunctionFileSchemaFields(userInputSchema, fileInputs));
	const uiSchema = createCustomFunctionUserInputUiSchema(userInputPresentations);
	const [formData, setFormData] = useState(() => createInitialCustomFunctionUiActionUserInput(schema, undefined));
	const [continuationResult, setContinuationResult] = useState<CustomFunctionUiActionExecutionResult | null>(null);
	const formRef = useRef<JsonSchemaObjectFormRef | null>(null);
	const editedRef = useRef(false);
	const fileInputsRef = useRef<CustomFunctionFileInputsRef | null>(null);
	const idempotencySessionRef = useRef<CustomFunctionUiActionIdempotencySession | null>(null);
	if (idempotencySessionRef.current == null) {
		idempotencySessionRef.current = createCustomFunctionUiActionIdempotencySession(
			principalId,
			environmentId,
			triggerLineageId,
			context,
		);
	}

	if (continuationResult != null) {
		return (
			<CustomFunctionUiActionContinuation
				initialResult={continuationResult}
				closeDialog={closeDialog}
				onFinished={finishExecution}
			/>
		);
	}

	return (
		<VerticalBox sx={{ flex: 1, minHeight: 0 }}>
			<Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", padding: 2 }}>
				<JsonSchemaObjectForm
					ref={formRef}
					schema={schema}
					uiSchema={uiSchema}
					formData={formData}
					onChange={changeInput}
				/>
				<CustomFunctionFileInputs
					ref={fileInputsRef}
					definitions={fileInputs}
					inputSchema={userInputSchema}
					onEdited={markEdited}
				/>
			</Box>
			<AavoFormActionButtons
				onCancel={() => closeDialog({ confirmIfEdited: true })}
				onSubmit={execute}
				submitLabel={i18n.t("run_action")}
			/>
		</VerticalBox>
	);

	async function execute() {
		if (formRef.current?.validate() !== true) return;
		const selectedFiles = fileInputsRef.current?.getValidatedFiles();
		if (selectedFiles == null) return;
		const idempotencyKey = await idempotencySessionRef.current?.getKey();
		if (idempotencyKey == null) return;
		const result = await CustomFunctionUiActionApi.executeUiAction({
			triggerLineageId,
			context,
			idempotencyKey,
			userInput: prepareCustomFunctionUiActionUserInput(schema, formData),
			fileInputs: await uploadCustomFunctionInputFiles(fileInputs, selectedFiles),
		});
		await idempotencySessionRef.current?.clearKey(idempotencyKey);
		if (result.inputRequest != null) {
			setContinuationResult(result);
			return;
		}
		await finishExecution(result);
	}

	async function finishExecution(result: CustomFunctionUiActionExecutionResult) {
		await closeDialog();
		await onExecuted(result);
	}

	function markEdited() {
		idempotencySessionRef.current?.markEdited();
		if (editedRef.current) return;
		editedRef.current = true;
		onEdited();
	}

	function changeInput(value: JsonObject, fieldId?: string) {
		setFormData(toJsonObject(value));
		if (fieldId != null) markEdited();
	}
}
